import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Alert, Grid, Paper,
  Select, MenuItem, FormControl, InputLabel, Chip, IconButton, Tooltip,
  CircularProgress, LinearProgress, Divider, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Drawer,
  Autocomplete, TextField, alpha, useTheme, Switch, FormControlLabel,
  Container, Skeleton, Collapse
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import axios from '../utils/axios';
import { useNotifications } from '../contexts/NotificationContext';

const CHART_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'];

/* Outline SVG icons */
const DollarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2v16" strokeLinecap="round" />
    <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TrendUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 17l5-5 3 3 6-8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 7h3v3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TrendDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 3l5 5 3-3 6 8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 13h3v-3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 4h14M5 8h10M7 12h6M9 16h2" strokeLinecap="round" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 10a7 7 0 0112.9-3.7M17 10a7 7 0 01-12.9 3.7" strokeLinecap="round" />
    <path d="M16 3v4h-4M4 17v-4h4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ForecastIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 16l4-4 3 2 4-5 5-3" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="17" cy="4" r="2" />
  </svg>
);
const SavingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M10 2l2 4h4l-3 3 1 5-4-2-4 2 1-5-3-3h4l2-4z" strokeLinejoin="round" />
  </svg>
);
const ServiceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="11" y="3" width="6" height="6" rx="1" />
    <rect x="3" y="11" width="6" height="6" rx="1" />
    <rect x="11" y="11" width="6" height="6" rx="1" />
  </svg>
);

function CostOptimization() {
  const theme = useTheme();
  const { addNotification } = useNotifications();

  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [costData, setCostData] = useState(null);
  const [timeRange, setTimeRange] = useState('30');

  // Filters
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [groupBy, setGroupBy] = useState('SERVICE');
  const [granularity, setGranularity] = useState('DAILY');
  const [showUnblendedCost, setShowUnblendedCost] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Service drill-down drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceResources, setServiceResources] = useState(null);
  const [isLoadingResources, setIsLoadingResources] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisCached, setAnalysisCached] = useState(false);
  const [analysisCachedAt, setAnalysisCachedAt] = useState(null);
  const [isLoadingCachedAnalysis, setIsLoadingCachedAnalysis] = useState(false);

  // Chatbot state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatServiceContext, setChatServiceContext] = useState(null);
  const chatEndRef = React.useRef(null);

  useEffect(() => { loadAWSAccounts(); }, []);
  useEffect(() => { if (selectedAccount) loadAvailableFilters(); }, [selectedAccount]);
  useEffect(() => {
    if (selectedAccount) {
      if (timeRange === 'custom') {
        if (customStartDate && customEndDate) loadCostData();
      } else {
        loadCostData();
      }
    }
  }, [selectedAccount, timeRange, customStartDate, customEndDate, selectedServices, selectedRegions, groupBy, granularity, showUnblendedCost]);

  const loadAWSAccounts = async () => {
    try {
      const res = await axios.get('/api/aws/accounts');
      const accounts = res.data.accounts || [];
      setAwsAccounts(accounts);
      if (accounts.length > 0) setSelectedAccount(accounts[0].id);
    } catch (err) {
      console.error('Failed to load AWS accounts:', err);
    }
  };

  const loadAvailableFilters = async () => {
    if (!selectedAccount) return;
    setIsLoadingFilters(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const start = new Date();
      start.setDate(start.getDate() - parseInt(timeRange || '30'));
      const startDate = start.toISOString().split('T')[0];
      const res = await axios.get(`/api/cost/filters/${selectedAccount}?startDate=${startDate}&endDate=${endDate}`);
      setAvailableServices(res.data.services || []);
      setAvailableRegions(res.data.regions || []);
    } catch (err) {
      console.error('Failed to load filters:', err);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const loadCostData = async () => {
    if (!selectedAccount) return;
    if (timeRange === 'custom' && (!customStartDate || !customEndDate)) return;
    setIsLoading(true);
    setCostData(null);
    try {
      const params = new URLSearchParams({
        groupBy, granularity,
        costType: showUnblendedCost ? 'UNBLENDED' : 'BLENDED'
      });
      if (timeRange === 'custom') {
        params.append('startDate', customStartDate);
        params.append('endDate', customEndDate);
      } else {
        params.append('timeRange', timeRange);
      }
      if (selectedServices.length > 0) params.append('services', selectedServices.join(','));
      if (selectedRegions.length > 0) params.append('regions', selectedRegions.join(','));
      const res = await axios.get(`/api/cost/billing/${selectedAccount}?${params}`);
      const data = res.data;
      // Backend may return { error: 'NO_DATA', billingData: {...} } on 200
      // or return billingData fields at root level on success
      if (data.error && data.billingData) {
        // NO_DATA response — keep the error info but flatten billingData to root
        setCostData({ ...data.billingData, error: data.error, message: data.message, setupInstructions: data.setupInstructions });
      } else if (data.error) {
        setCostData({ error: data.error, message: data.message, setupInstructions: data.setupInstructions });
      } else {
        setCostData(data);
      }
    } catch (err) {
      console.error('Failed to load cost data:', err);
      const errData = err.response?.data;
      setCostData({
        error: errData?.error || 'NETWORK_ERROR',
        message: errData?.message || err.message || 'Failed to load cost data',
        details: errData?.details,
        setupInstructions: errData?.setupInstructions
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleServiceClick = async (serviceName) => {
    if (groupBy !== 'SERVICE') return;
    setSelectedService(serviceName);
    setDrawerOpen(true);
    setServiceResources(null);
    setAiAnalysis(null);
    setAnalysisCached(false);
    setAnalysisCachedAt(null);
    setIsLoadingResources(true);
    setIsLoadingCachedAnalysis(true);
    try {
      const trParam = timeRange === 'custom' ? `startDate=${customStartDate}&endDate=${customEndDate}` : `timeRange=${timeRange}`;
      const [resourcesRes, cachedRes] = await Promise.allSettled([
        axios.get(`/api/cost/service-resources/${selectedAccount}?service=${encodeURIComponent(serviceName)}&${trParam}`),
        axios.get(`/api/cost/optimize/${selectedAccount}?service=${encodeURIComponent(serviceName)}`)
      ]);
      if (resourcesRes.status === 'fulfilled') {
        setServiceResources(resourcesRes.value.data);
      } else {
        setServiceResources({ error: resourcesRes.reason?.response?.data?.error || resourcesRes.reason?.message });
      }
      if (cachedRes.status === 'fulfilled' && cachedRes.value.data?.analysis) {
        setAiAnalysis(cachedRes.value.data.analysis);
        setAnalysisCached(true);
        setAnalysisCachedAt(cachedRes.value.data.cachedAt);
      }
    } catch (err) {
      console.error('Failed to load service data:', err);
      setServiceResources({ error: err.response?.data?.error || err.message });
    } finally {
      setIsLoadingResources(false);
      setIsLoadingCachedAnalysis(false);
    }
  };

  const handleRunAnalysis = async (forceRefresh = false) => {
    if (!serviceResources?.resources || !selectedService) return;
    setIsAnalyzing(true);
    setAiAnalysis(null);
    setAnalysisCached(false);
    setAnalysisCachedAt(null);
    try {
      const res = await axios.post(`/api/cost/optimize/${selectedAccount}`, {
        service: selectedService,
        resources: serviceResources.resources,
        totalCost: serviceResources.totalCost,
        forceRefresh
      });
      setAiAnalysis(res.data.analysis);
      setAnalysisCached(!!res.data.cached);
      setAnalysisCachedAt(res.data.cachedAt || null);
    } catch (err) {
      console.error('AI analysis failed:', err);
      setAiAnalysis({ error: err.response?.data?.error || err.message });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedService(null);
    setServiceResources(null);
    setAiAnalysis(null);
    setAnalysisCached(false);
    setAnalysisCachedAt(null);
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || !selectedAccount || isChatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsChatLoading(true);
    try {
      const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
      const res = await axios.post(`/api/cost/chat/${selectedAccount}`, {
        message: userMsg,
        conversationHistory: history,
        serviceContext: chatServiceContext
      });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: err.response?.data?.error || 'Something went wrong. Please try again.' }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const openChatForService = (serviceName, resources, totalCost, summary) => {
    const ctx = {
      serviceName,
      totalCost: totalCost || 0,
      resourceCount: resources?.length || 0,
      resources: (resources || []).slice(0, 10),
      aiSummary: summary || null
    };
    setChatServiceContext(ctx);
    setChatMessages([]);
    setChatInput('');
    setChatOpen(true);
  };

  const openChatGlobal = () => {
    setChatServiceContext(null);
    setChatMessages([]);
    setChatInput('');
    setChatOpen(true);
  };

  React.useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isChatLoading]);

  const fmt = (amount) => {
    const val = amount || 0;
    // Show more decimal places for very small amounts
    if (val > 0 && val < 0.01) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(val);
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
  };

  const fmtPct = (v) => `${v >= 0 ? '+' : ''}${(v || 0).toFixed(1)}%`;

  // Prepare chart data from costBreakdown
  const breakdownChartData = useMemo(() => {
    if (!costData?.costBreakdown) return [];
    return Object.entries(costData.costBreakdown)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([name, cost]) => ({
        name: name.length > 25 ? name.substring(0, 22) + '...' : name,
        fullName: name,
        cost: parseFloat(cost.toFixed(2)),
        trend: costData.trends?.[name] || 0
      }));
  }, [costData]);

  // Pie chart data
  const pieData = useMemo(() => {
    if (!costData?.costBreakdown) return [];
    const entries = Object.entries(costData.costBreakdown).sort(([, a], [, b]) => b - a);
    const total = entries.reduce((s, [, v]) => s + v, 0);
    const top5 = entries.slice(0, 5);
    const otherCost = entries.slice(5).reduce((s, [, v]) => s + v, 0);
    const result = top5.map(([name, cost]) => ({
      name: name.length > 20 ? name.substring(0, 17) + '...' : name,
      value: parseFloat(cost.toFixed(2)),
      pct: total > 0 ? ((cost / total) * 100).toFixed(1) : '0'
    }));
    if (otherCost > 0) {
      result.push({ name: 'Others', value: parseFloat(otherCost.toFixed(2)), pct: total > 0 ? ((otherCost / total) * 100).toFixed(1) : '0' });
    }
    return result;
  }, [costData]);

  // Fastest growing services — only include services with meaningful cost (> $0.01)
  const fastestGrowing = useMemo(() => {
    if (!costData?.trends || !costData?.costBreakdown) return [];
    return Object.entries(costData.trends)
      .filter(([key]) => (costData.costBreakdown[key] || 0) >= 0.01)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name, trend]) => ({
        name,
        cost: costData.costBreakdown[name] || 0,
        trend
      }));
  }, [costData]);

  // Table data with distribution bars
  const tableData = useMemo(() => {
    if (!costData?.costBreakdown) return [];
    const total = Object.values(costData.costBreakdown).reduce((s, v) => s + v, 0);
    return Object.entries(costData.costBreakdown)
      .sort(([, a], [, b]) => b - a)
      .map(([name, cost]) => ({
        name,
        cost,
        pct: total > 0 ? (cost / total) * 100 : 0,
        trend: costData.trends?.[name] || 0
      }));
  }, [costData]);

  const hasData = costData && !costData.error && (costData.totalCost > 0 || Object.keys(costData.costBreakdown || {}).length > 0);
  const hasError = costData?.error;
  const noAccounts = awsAccounts.length === 0 && !isLoading;
  const dataLoaded = costData !== null && !isLoading;

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', boxShadow: 2 }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>{label || payload[0]?.name}</Typography>
        {payload.map((entry, i) => (
          <Typography key={i} sx={{ fontSize: '0.8125rem', fontWeight: 600, color: entry.color || 'text.primary' }}>
            {fmt(entry.value)}
          </Typography>
        ))}
      </Paper>
    );
  };

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, pct }) => {
    if (parseFloat(pct) < 5) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {pct}%
      </text>
    );
  };

  // Stat card component
  const StatCard = ({ icon, iconColor, iconBg, title, value, trend, trendLabel, loading: cardLoading }) => (
    <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '8px',
            bgcolor: theme.palette.mode === 'light' ? iconBg : alpha(iconColor, 0.15),
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor
          }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500 }}>{title}</Typography>
        </Box>
        {cardLoading ? (
          <Skeleton width={100} height={36} />
        ) : (
          <>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>{value}</Typography>
            {trend !== undefined && trend !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ color: trend >= 0 ? '#EF4444' : '#10B981', display: 'flex', alignItems: 'center' }}>
                  {trend >= 0 ? <TrendUpIcon /> : <TrendDownIcon />}
                </Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: trend >= 0 ? '#EF4444' : '#10B981' }}>
                  {fmtPct(trend)}
                </Typography>
                {trendLabel && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>{trendLabel}</Typography>
                )}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', mb: 0.25 }}>FinOps</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>AWS cost analysis and optimization</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {awsAccounts.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}
                sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                {awsAccounts.map(a => (
                  <MenuItem key={a.id} value={a.id} sx={{ fontSize: '0.8125rem' }}>
                    {a.account_name || a.account_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}
              sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="14">Last 14 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="60">Last 60 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
          {timeRange === 'custom' && (
            <>
              <TextField size="small" type="date" label="Start" value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: customEndDate || new Date().toISOString().split('T')[0] }}
                sx={{ width: 150, '& .MuiInputBase-root': { fontSize: '0.8125rem', borderRadius: '8px' } }} />
              <TextField size="small" type="date" label="End" value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customStartDate, max: new Date().toISOString().split('T')[0] }}
                sx={{ width: 150, '& .MuiInputBase-root': { fontSize: '0.8125rem', borderRadius: '8px' } }} />
            </>
          )}
          <Tooltip title="Filters">
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)}
              sx={{ border: '1px solid', borderColor: showFilters ? 'primary.main' : 'divider', borderRadius: '8px',
                bgcolor: showFilters ? alpha(theme.palette.primary.main, 0.08) : 'transparent' }}>
              <FilterIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadCostData} disabled={isLoading}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter Panel */}
      <Collapse in={showFilters}>
        <Card elevation={0} sx={{ mb: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8125rem' }}>Group By</InputLabel>
                  <Select value={groupBy} label="Group By" onChange={(e) => setGroupBy(e.target.value)}
                    sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                    <MenuItem value="SERVICE">Service</MenuItem>
                    <MenuItem value="REGION">Region</MenuItem>
                    <MenuItem value="LINKED_ACCOUNT">Account</MenuItem>
                    <MenuItem value="USAGE_TYPE">Usage Type</MenuItem>
                    <MenuItem value="INSTANCE_TYPE">Instance Type</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8125rem' }}>Granularity</InputLabel>
                  <Select value={granularity} label="Granularity" onChange={(e) => setGranularity(e.target.value)}
                    sx={{ fontSize: '0.8125rem', borderRadius: '8px' }}>
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete multiple size="small" options={availableServices} value={selectedServices}
                  onChange={(_, v) => setSelectedServices(v)} loading={isLoadingFilters}
                  renderInput={(params) => <TextField {...params} label="Services" sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem', borderRadius: '8px' } }} />}
                  renderTags={(value, getTagProps) => value.map((opt, i) => (
                    <Chip {...getTagProps({ index: i })} key={opt} label={opt.length > 15 ? opt.substring(0, 12) + '...' : opt} size="small" sx={{ fontSize: '0.6875rem' }} />
                  ))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete multiple size="small" options={availableRegions} value={selectedRegions}
                  onChange={(_, v) => setSelectedRegions(v)} loading={isLoadingFilters}
                  renderInput={(params) => <TextField {...params} label="Regions" sx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem', borderRadius: '8px' } }} />}
                  renderTags={(value, getTagProps) => value.map((opt, i) => (
                    <Chip {...getTagProps({ index: i })} key={opt} label={opt} size="small" sx={{ fontSize: '0.6875rem' }} />
                  ))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch size="small" checked={showUnblendedCost} onChange={(e) => setShowUnblendedCost(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.8125rem' }}>Show Unblended Cost</Typography>}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* No accounts state */}
      {noAccounts && (
        <Alert severity="info" sx={{ borderRadius: '8px' }}>
          Connect an AWS account to view cost data. Go to AWS Accounts to get started.
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Loading cost data from AWS Cost Explorer...</Typography>
        </Box>
      )}

      {/* Error / empty state */}
      {hasError && !isLoading && (
        <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '12px', mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: costData.error === 'NO_DATA' ? alpha('#2563EB', 0.1) : alpha('#F59E0B', 0.1),
              color: costData.error === 'NO_DATA' ? '#2563EB' : '#F59E0B' }}>
              <DollarIcon />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>
              {costData.error === 'NO_DATA' ? 'No Cost Data Available' :
               costData.error === 'PERMISSION_DENIED' ? 'Permission Required' :
               costData.error === 'NOT_ENABLED' ? 'Cost Explorer Not Enabled' :
               'Unable to Load Cost Data'}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2, maxWidth: 500, mx: 'auto' }}>
              {costData.message || 'An error occurred while loading cost data.'}
            </Typography>
            {costData.setupInstructions?.length > 0 && (
              <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', bgcolor: alpha(theme.palette.text.primary, 0.03), textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
                {costData.setupInstructions.map((inst, i) => (
                  <Typography key={i} sx={{ fontSize: '0.8125rem', color: 'text.secondary', whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.8 }}>
                    {inst}
                  </Typography>
                ))}
              </Paper>
            )}
            {costData.error !== 'NO_DATA' && (
              <Button variant="outlined" size="small" onClick={loadCostData} sx={{ mt: 2, borderRadius: '8px', textTransform: 'none' }}>
                Try Again
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Fallback: data loaded, no error, but nothing to show */}
      {dataLoaded && !hasData && !hasError && (
        <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '12px', mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha('#2563EB', 0.1), color: '#2563EB' }}>
              <DollarIcon />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>
              No Cost Data Found
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>
              Cost Explorer returned no spending data for the selected period. Try a longer time range.
            </Typography>
            <Button variant="outlined" size="small" onClick={loadCostData} sx={{ borderRadius: '8px', textTransform: 'none' }}>
              Refresh
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Main content - only when we have data */}
      {hasData && !isLoading && (
        <>
          {/* Stat Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<DollarIcon />} iconColor="#2563EB" iconBg="#EFF6FF"
                title={timeRange === 'custom' ? `Cost (${customStartDate} – ${customEndDate})` : `MTD Cost (${parseInt(timeRange)}d)`}
                value={fmt(costData.totalCost)}
                trend={costData.costTrend}
                trendLabel="vs prev period"
              />
            </Grid>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<SavingsIcon />} iconColor="#10B981" iconBg="#ECFDF5"
                title="Services Tracked"
                value={Object.keys(costData.costBreakdown || {}).length.toString()}
                trend={null}
              />
            </Grid>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<ForecastIcon />} iconColor="#8B5CF6" iconBg="#F5F3FF"
                title="30d Forecast"
                value={fmt(costData.forecastedCost)}
                trend={costData.forecastTrend}
                trendLabel="projected"
              />
            </Grid>
            <Grid item xs={6} lg={3}>
              <StatCard
                icon={<ServiceIcon />} iconColor="#F59E0B" iconBg="#FFFBEB"
                title="Top Service"
                value={costData.topService ? costData.topService.name.length > 18 ? costData.topService.name.substring(0, 15) + '...' : costData.topService.name : '-'}
                trend={costData.topService ? costData.trends?.[costData.topService.name] : null}
                trendLabel={costData.topService ? fmt(costData.topService.cost) : ''}
              />
            </Grid>
          </Grid>

          {/* Charts Row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Cost by Service - Bar Chart */}
            <Grid item xs={12} lg={8}>
              <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 2 }}>
                    Cost by {groupBy === 'SERVICE' ? 'Service' : groupBy === 'REGION' ? 'Region' : groupBy.replace('_', ' ').toLowerCase()}
                  </Typography>
                  <Box sx={{ height: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={breakdownChartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                        <XAxis type="number" tickFormatter={(v) => `$${v.toFixed(0)}`}
                          tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                        <YAxis type="category" dataKey="name" width={150}
                          tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Bar dataKey="cost" radius={[0, 4, 4, 0]} maxBarSize={28}>
                          {breakdownChartData.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Cost Distribution - Pie Chart */}
            <Grid item xs={12} lg={4}>
              <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 2 }}>
                    Cost Distribution
                  </Typography>
                  <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={95}
                          dataKey="value" paddingAngle={2}
                          label={CustomPieLabel} labelLine={false}>
                          {pieData.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="bottom"
                          formatter={(value) => <span style={{ fontSize: '0.6875rem', color: theme.palette.text.secondary }}>{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Fastest Growing + Cost Breakdown Table */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Fastest Growing Services */}
            <Grid item xs={12} lg={4}>
              <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 2 }}>
                    Fastest Growing Services
                  </Typography>
                  {fastestGrowing.length === 0 ? (
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', py: 4, textAlign: 'center' }}>
                      No services with significant cost changes
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {fastestGrowing.map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1,
                          borderBottom: i < fastestGrowing.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{fmt(item.cost)}</Typography>
                          </Box>
                          <Chip
                            size="small"
                            icon={item.trend >= 0 ? <TrendUpIcon /> : <TrendDownIcon />}
                            label={fmtPct(item.trend)}
                            sx={{
                              fontSize: '0.6875rem', fontWeight: 600, ml: 1,
                              bgcolor: item.trend >= 0 ? alpha('#EF4444', 0.1) : alpha('#10B981', 0.1),
                              color: item.trend >= 0 ? '#EF4444' : '#10B981',
                              '& .MuiChip-icon': { color: 'inherit' }
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Full Cost Breakdown Table */}
            <Grid item xs={12} lg={8}>
              <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                      Cost Breakdown
                    </Typography>
                    <Chip label={`${tableData.length} items`} size="small"
                      sx={{ fontSize: '0.6875rem', bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main' }} />
                  </Box>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>
                            {groupBy === 'SERVICE' ? 'Service' : groupBy === 'REGION' ? 'Region' : 'Name'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>Cost</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper', minWidth: 120 }}>Share</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper' }}>Trend</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableData.map((row, i) => (
                          <TableRow key={i} hover={groupBy === 'SERVICE'}
                            onClick={() => groupBy === 'SERVICE' && handleServiceClick(row.name)}
                            sx={{ '&:last-child td': { borderBottom: 0 }, cursor: groupBy === 'SERVICE' ? 'pointer' : 'default',
                              '&:hover': groupBy === 'SERVICE' ? { bgcolor: alpha(theme.palette.primary.main, 0.04) } : {} }}>
                            <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              color: groupBy === 'SERVICE' ? 'primary.main' : 'text.primary' }}>
                              <Tooltip title={groupBy === 'SERVICE' ? `Click to view ${row.name} resources` : row.name}><span>{row.name}</span></Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{fmt(row.cost)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                                  <Box sx={{ height: '100%', borderRadius: 3, width: `${Math.min(row.pct, 100)}%`,
                                    bgcolor: CHART_COLORS[i % CHART_COLORS.length], transition: 'width 0.5s ease' }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', minWidth: 32, textAlign: 'right' }}>
                                  {row.pct.toFixed(1)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {row.trend !== 0 ? (
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: row.trend >= 0 ? '#EF4444' : '#10B981' }}>
                                  {fmtPct(row.trend)}
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Service Drill-Down Drawer */}
      {drawerOpen && (
        <Box sx={{
          position: 'fixed', top: 0, right: 0, width: { xs: '100%', md: '70%' }, height: '100vh',
          bgcolor: 'background.paper', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 1300,
          display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          {/* Drawer Header */}
          <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'text.primary' }}>{selectedService}</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                {serviceResources ? `${serviceResources.resourceCount || 0} usage types · ${fmt(serviceResources.totalCost)}` : 'Loading...'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseDrawer} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
              </svg>
            </IconButton>
          </Box>

          {/* Drawer Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            {isLoadingResources && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={32} sx={{ mb: 1.5 }} />
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>Loading resource breakdown...</Typography>
              </Box>
            )}

            {serviceResources?.error && (
              <Alert severity="warning" sx={{ borderRadius: '8px', mb: 2 }}>
                {serviceResources.error}
              </Alert>
            )}

            {serviceResources?.resources && (
              <>
                {/* Resource Usage Table */}
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 1.5 }}>Resource Usage</Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Usage Type</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Cost</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Usage</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Avg/Day</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceResources.resources.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontSize: '0.8125rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Tooltip title={r.usageType}><span>{r.usageType}</span></Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{fmt(r.totalCost)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{r.totalUsage.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{fmt(r.avgDailyCost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* AI Optimization Section */}
                <Divider sx={{ mb: 2 }} />
                {serviceResources.totalCost < 1 ? (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                      AI analysis is available for services with costs of $1.00 or more.
                    </Typography>
                  </Box>
                ) : (
                  <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>AI Optimization Analysis</Typography>
                    {analysisCached && analysisCachedAt && (
                      <Chip size="small" label={`Cached · ${new Date(analysisCachedAt).toLocaleDateString()}`}
                        sx={{ fontSize: '0.625rem', height: 20, bgcolor: alpha('#8B5CF6', 0.1), color: '#8B5CF6', fontWeight: 600 }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {aiAnalysis && !aiAnalysis.error && !isAnalyzing && (
                      <Button variant="outlined" size="small" onClick={() => handleRunAnalysis(true)} disableElevation
                        sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem' }}>
                        Re-analyze
                      </Button>
                    )}
                    {!aiAnalysis && !isAnalyzing && !isLoadingCachedAnalysis && (
                      <Button variant="contained" size="small" onClick={() => handleRunAnalysis(false)} disableElevation
                        sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.8125rem' }}>
                        Analyze with AI
                      </Button>
                    )}
                  </Box>
                </Box>

                {isLoadingCachedAnalysis && !aiAnalysis && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ mb: 0.5 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Checking for cached analysis...</Typography>
                  </Box>
                )}

                {isAnalyzing && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ mb: 1 }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>Analyzing with Bedrock AI...</Typography>
                  </Box>
                )}

                {aiAnalysis?.error && (
                  <Alert severity="warning" sx={{ borderRadius: '8px' }}>{aiAnalysis.error}</Alert>
                )}

                {aiAnalysis && !aiAnalysis.error && (
                  <Stack spacing={2.5}>
                    {/* Summary */}
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>Summary</Typography>
                      <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', bgcolor: alpha('#2563EB', 0.04), border: '1px solid', borderColor: alpha('#2563EB', 0.15) }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary', lineHeight: 1.7 }}>{aiAnalysis.summary}</Typography>
                        {(aiAnalysis.estimatedMonthlySavings > 0) && (
                          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                            <Chip size="small" label={`Potential Savings: ${fmt(aiAnalysis.estimatedMonthlySavings)}/mo`}
                              sx={{ bgcolor: alpha('#10B981', 0.1), color: '#10B981', fontWeight: 600, fontSize: '0.75rem' }} />
                          </Box>
                        )}
                      </Paper>
                    </Box>

                    {/* Recommendations */}
                    {aiAnalysis.recommendations && (
                      <Box>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>Recommendations</Typography>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.7 }}>
                            {typeof aiAnalysis.recommendations === 'string' ? aiAnalysis.recommendations :
                              Array.isArray(aiAnalysis.recommendations) ? aiAnalysis.recommendations.map(r => r.description || r.title).join(' ') : ''}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {/* Resource Insights Table */}
                    {aiAnalysis.resourceInsights?.length > 0 && (
                      <Box>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>Resource Insights</Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
                                <TableCell sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Resource</TableCell>
                                <TableCell sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Current Configuration</TableCell>
                                <TableCell sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Recommended Configuration</TableCell>
                                <TableCell sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Remediation Command</TableCell>
                                <TableCell sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Impact Assessment</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {aiAnalysis.resourceInsights.map((ins, i) => (
                                <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 }, verticalAlign: 'top' }}>
                                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', minWidth: 120, maxWidth: 160, wordBreak: 'break-word' }}>
                                    {ins.resource || ins.usageType}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 120, maxWidth: 180, wordBreak: 'break-word' }}>
                                    {ins.currentConfig || '-'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', minWidth: 120, maxWidth: 180, wordBreak: 'break-word',
                                    color: (ins.optimalConfig && ins.optimalConfig !== 'No change required') ? '#2563EB' : 'text.secondary' }}>
                                    {ins.optimalConfig || '-'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.6875rem', fontFamily: 'monospace', minWidth: 140, maxWidth: 200, wordBreak: 'break-all' }}>
                                    {ins.awsCli && ins.awsCli !== 'N/A' ? (
                                      <Box sx={{ position: 'relative' }}>
                                        <Box sx={{ p: 1, borderRadius: '4px', bgcolor: alpha(theme.palette.text.primary, 0.04), fontSize: '0.6875rem', lineHeight: 1.4 }}>
                                          {ins.awsCli}
                                        </Box>
                                        <IconButton size="small" onClick={() => { navigator.clipboard.writeText(ins.awsCli); }}
                                          sx={{ position: 'absolute', top: 0, right: 0, width: 20, height: 20 }}>
                                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <rect x="5" y="5" width="9" height="9" rx="1" />
                                            <path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" />
                                          </svg>
                                        </IconButton>
                                      </Box>
                                    ) : (
                                      <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>N/A</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', minWidth: 100, maxWidth: 160, wordBreak: 'break-word' }}>
                                    <Chip size="small" label={ins.impact || 'No downtime'}
                                      sx={{ fontSize: '0.625rem', height: 20, fontWeight: 600,
                                        bgcolor: (ins.impact?.toLowerCase().includes('no downtime') || ins.impact?.toLowerCase().includes('no change') || ins.impact === 'N/A')
                                          ? alpha('#10B981', 0.1) : alpha('#F59E0B', 0.1),
                                        color: (ins.impact?.toLowerCase().includes('no downtime') || ins.impact?.toLowerCase().includes('no change') || ins.impact === 'N/A')
                                          ? '#10B981' : '#F59E0B'
                                      }} />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Stack>
                )}
                  </>
                )}

                {/* Chat about this service button */}
                <Divider sx={{ my: 2 }} />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => openChatForService(selectedService, serviceResources?.resources, serviceResources?.totalCost, aiAnalysis?.summary)}
                  startIcon={
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 4h12a2 2 0 012 2v6a2 2 0 01-2 2H8l-4 3v-3a2 2 0 01-2-2V6a2 2 0 012-2z" strokeLinejoin="round" />
                    </svg>
                  }
                  sx={{
                    borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)' }
                  }}
                >
                  Chat about {selectedService}
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Backdrop */}
      {drawerOpen && (
        <Box onClick={handleCloseDrawer} sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(0,0,0,0.3)', zIndex: 1299
        }} />
      )}

      {/* Astra FinOps Chat Button — hidden when chat drawer is open */}
      {selectedAccount && !chatOpen && (
        <Tooltip title="Astra FinOps">
          <IconButton onClick={openChatGlobal}
            sx={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 1400,
              width: 52, height: 52, borderRadius: '16px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
              color: '#fff', boxShadow: '0 4px 14px rgba(124,58,237,0.4)',
              '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)' }
            }}>
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4h12a2 2 0 012 2v6a2 2 0 01-2 2H8l-4 3v-3a2 2 0 01-2-2V6a2 2 0 012-2z" strokeLinejoin="round" />
              <circle cx="7" cy="9" r="0.5" fill="currentColor" />
              <circle cx="10" cy="9" r="0.5" fill="currentColor" />
              <circle cx="13" cy="9" r="0.5" fill="currentColor" />
            </svg>
          </IconButton>
        </Tooltip>
      )}

      {/* Astra FinOps Chat Drawer */}
      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        sx={{ zIndex: 1500 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            borderRadius: '16px 0 0 16px',
            boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Chat Header */}
        <Box sx={{
          p: 2.5, borderBottom: '1px solid', borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(37,99,235,0.06) 100%)'
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5">
                  <path d="M10 2v16" strokeLinecap="round" />
                  <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Astra FinOps
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {chatServiceContext ? chatServiceContext.serviceName : 'Ask about your AWS costs & billing'}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setChatOpen(false)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
              </svg>
            </IconButton>
          </Box>
          {/* Service context badge */}
          {chatServiceContext && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.12)' }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  label={fmt(chatServiceContext.totalCost)}
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#2563EB', 0.1), color: '#2563EB' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {chatServiceContext.resourceCount} usage type{chatServiceContext.resourceCount !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <Button
                    size="small"
                    onClick={() => { setChatServiceContext(null); setChatMessages([]); }}
                    sx={{ fontSize: '0.65rem', textTransform: 'none', minWidth: 0, px: 1, color: 'text.secondary' }}
                  >
                    Switch to global
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Chat Messages */}
        <Box sx={{
          flex: 1, overflow: 'auto', p: 2.5,
          display: 'flex', flexDirection: 'column', gap: 2,
          bgcolor: alpha(theme.palette.grey[500], 0.02)
        }}>
          {/* Welcome message */}
          {chatMessages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
                background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(37,99,235,0.1) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 20 20" fill="none" stroke="#7c3aed" strokeWidth="1.5">
                  <path d="M10 2v16" strokeLinecap="round" />
                  <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {chatServiceContext
                  ? `Ask me about "${chatServiceContext.serviceName}"`
                  : 'How can I help with your AWS costs?'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                {chatServiceContext
                  ? 'I have full context of this service, its usage types, costs, and optimization opportunities.'
                  : 'I have full context of your AWS billing data, service costs, and spending trends.'}
              </Typography>
              <Stack spacing={1}>
                {(chatServiceContext ? [
                  `How can I reduce ${chatServiceContext.serviceName} costs?`,
                  'Are there any Reserved Instance or Savings Plan options?',
                  'What usage types are driving the most cost?',
                  'Is there any idle or underutilized capacity?',
                  'What would switching to a different pricing model save?'
                ] : [
                  'What are my top spending services this month?',
                  'How can I reduce my AWS bill?',
                  'Which services are growing fastest in cost?',
                  'What Reserved Instances should I consider?'
                ]).map((suggestion, idx) => (
                  <Box
                    key={idx}
                    onClick={() => { setChatInput(suggestion); }}
                    sx={{
                      p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(124,58,237,0.03)' }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {suggestion}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Message bubbles */}
          {chatMessages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 1
              }}
            >
              {msg.role === 'assistant' && (
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0, mt: 0.5,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5">
                    <path d="M10 2v16" strokeLinecap="round" />
                    <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Box>
              )}
              <Box sx={{
                maxWidth: '85%',
                p: 2,
                borderRadius: 2.5,
                ...(msg.role === 'user' ? {
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderBottomRightRadius: 4
                } : {
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderBottomLeftRadius: 4
                })
              }}>
                {msg.role === 'user' ? (
                  <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                ) : (
                  <Box sx={{
                    '& p': { m: 0, mb: 1, lineHeight: 1.7, fontSize: '0.875rem', '&:last-child': { mb: 0 } },
                    '& ul, & ol': { m: 0, mb: 1, pl: 2.5, '&:last-child': { mb: 0 } },
                    '& li': { mb: 0.5, fontSize: '0.875rem', lineHeight: 1.6, '&::marker': { color: 'text.secondary' } },
                    '& strong': { fontWeight: 600 },
                    '& code': {
                      bgcolor: 'grey.100', color: 'text.primary',
                      px: 0.75, py: 0.25, borderRadius: 0.5,
                      fontSize: '0.8rem', fontFamily: 'monospace'
                    },
                    '& pre': {
                      bgcolor: '#1e1e1e', color: '#e0e0e0',
                      p: 1.5, borderRadius: 1.5, overflow: 'auto',
                      mb: 1, mt: 0.5,
                      '& code': { bgcolor: 'transparent', color: 'inherit', p: 0, fontSize: '0.78rem' }
                    },
                    '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.5, fontWeight: 700, fontSize: '0.9rem' },
                    '& blockquote': {
                      borderLeft: '3px solid', borderColor: 'divider',
                      pl: 1.5, ml: 0, my: 1, color: 'text.secondary', fontStyle: 'italic'
                    },
                    '& hr': { my: 1.5, borderColor: 'divider' },
                    '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                    '& table': { borderCollapse: 'collapse', width: '100%', mb: 1, fontSize: '0.8rem' },
                    '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
                    '& th': { bgcolor: 'grey.50', fontWeight: 600 }
                  }}>
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Box>
                )}
              </Box>
            </Box>
          ))}

          {/* Loading indicator */}
          {isChatLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5">
                  <path d="M10 2v16" strokeLinecap="round" />
                  <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" gap={0.5}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{
                      width: 6, height: 6, borderRadius: '50%', bgcolor: '#7c3aed',
                      animation: 'finopsPulse 1.4s infinite',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes finopsPulse': {
                        '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                        '40%': { opacity: 1, transform: 'scale(1)' }
                      }
                    }} />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
          <div ref={chatEndRef} />
        </Box>

        {/* Chat Input */}
        <Box sx={{
          p: 2, borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              size="small"
              placeholder={chatServiceContext ? `Ask about ${chatServiceContext.serviceName}...` : 'Ask about your AWS costs...'}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
              disabled={isChatLoading}
              multiline
              maxRows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  fontSize: '0.875rem'
                }
              }}
            />
            <IconButton
              onClick={handleChatSend}
              disabled={!chatInput.trim() || isChatLoading}
              sx={{
                width: 40, height: 40, borderRadius: 2,
                background: chatInput.trim() ? 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)' : 'transparent',
                color: chatInput.trim() ? 'white' : 'text.disabled',
                '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)' },
                '&.Mui-disabled': { background: 'transparent' }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </Container>
  );
}

export default CostOptimization;
